<template>
  <div class="alert-feedback-controls" :class="{ 'flag-only': !auditMode }">
    <button
      v-if="auditMode"
      type="button"
      class="alert-feedback-btn"
      :class="{ active: isPositive, positive: isPositive }"
      :title="isPositive ? 'Positive (click to clear)' : 'Mark as positive'"
      :disabled="submitting"
      @click.stop="submitPositive"
    >
      <i :class="isPositive ? 'fa fa-thumbs-up' : 'fa fa-thumbs-o-up'"></i>
    </button>
    <button
      v-if="auditMode"
      type="button"
      class="alert-feedback-btn"
      :class="{ active: isNegative, negative: isNegative }"
      :title="isNegative ? 'Negative (click to clear)' : 'Mark as negative'"
      :disabled="submitting"
      @click.stop="submitNegative"
    >
      <i :class="isNegative ? 'fa fa-thumbs-down' : 'fa fa-thumbs-o-down'"></i>
    </button>
    <button
      :id="flagButtonId"
      type="button"
      class="alert-feedback-btn"
      :class="{ active: isConcern, concern: isConcern }"
      :title="concernTitle"
      :disabled="submitting"
      @click.stop="openCommentModal"
    >
      <i :class="isConcern ? 'fa fa-flag' : 'fa fa-flag-o'"></i>
    </button>

    <b-popover
      :target="flagButtonId"
      :show.sync="commentModalVisible"
      triggers=""
      placement="auto"
      boundary="window"
      container="body"
      custom-class="concern-popover"
      @shown="focusCommentInput"
      @hidden="resetCommentModal"
    >
      <template #title>
        <div class="concern-popover-title-row">
          <span>Report An Issue</span>
          <button type="button" class="concern-popover-close-btn" aria-label="Cancel" :disabled="submitting" @click="commentModalVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </template>
      <b-form-textarea
        ref="commentInput"
        v-model="commentText"
        placeholder="Describe issues with these results..."
        rows="6"
        required
      ></b-form-textarea>
      <div class="concern-popover-hint">Comment required to submit.</div>
      <div class="concern-popover-actions">
        <b-button v-if="feedbackState !== 'none'" size="sm" variant="outline-danger" class="concern-popover-remove-btn" :disabled="submitting" @click="removeFeedback">
          Remove Feedback
        </b-button>
        <b-button size="sm" variant="primary" :disabled="submitting || !commentText.trim()" @click="submitConcern">
          Submit
        </b-button>
      </div>
    </b-popover>
  </div>
</template>

<script>
import moment from 'moment'
import { save_alert_feedback } from '../networking/apis'
import { alertFeedbackState } from '../helpers/alert_feedback_filter'

export default {
  name: 'alert_feedback_controls',

  props: {
    item: { type: Object, required: true },
    tenant: { type: String, required: true },
    auditMode: { type: Boolean, default: false },
  },

  data() {
    return {
      submitting: false,
      commentModalVisible: false,
      commentText: '',
    }
  },

  computed: {
    flagButtonId() {
      return `alert-flag-btn-${this._uid}`
    },
    feedbackComment() {
      return this.item && this.item.feedback_comment != null ? String(this.item.feedback_comment).trim() : ''
    },
    feedbackState() {
      return alertFeedbackState(this.item)
    },
    isPositive() {
      return this.feedbackState === 'positive'
    },
    isNegative() {
      return this.feedbackState === 'negative'
    },
    isConcern() {
      return this.feedbackState === 'concern'
    },
    concernTitle() {
      if (this.isConcern && this.feedbackComment)
        return this.feedbackComment
      return 'Report An Issue'
    },
  },

  methods: {
    async submitPositive() {
      if (this.isPositive) {
        await this.clearFeedback()
        return
      }
      await this.saveFeedback('positive')
    },
    async submitNegative() {
      if (this.isNegative) {
        await this.clearFeedback()
        return
      }
      await this.saveFeedback('negative')
    },
    openCommentModal() {
      this.commentText = this.isConcern ? this.feedbackComment : ''
      this.commentModalVisible = true
    },
    focusCommentInput() {
      const ref = this.$refs.commentInput
      if (ref && ref.focus)
        ref.focus()
    },
    resetCommentModal() {
      this.commentText = ''
    },
    // triggers="" (manual show/hide) means BootstrapVue never wires its own
    // outside-click dismissal, so this popover needs its own — bind while
    // open, and treat clicks on either the flag button or the popover's own
    // (teleported) content as "inside". BootstrapVue links the two via
    // aria-describedby on the button (set to the tip element's id), which
    // is a reliable way to find *this* instance's popover even though
    // multiple (hidden) tip elements from other rows can linger in the DOM.
    handleOutsideClick(event) {
      const target = event.target
      const button = document.getElementById(this.flagButtonId)
      if (button && button.contains(target))
        return
      const describedById = button && button.getAttribute('aria-describedby')
      const popoverEl = describedById ? document.getElementById(describedById) : null
      if (popoverEl && popoverEl.contains(target))
        return
      this.commentModalVisible = false
    },
    async submitConcern() {
      const trimmed = (this.commentText || '').trim()
      if (!trimmed)
        return
      await this.saveFeedback('concern', trimmed)
      this.commentModalVisible = false
    },
    async clearFeedback() {
      await this.saveFeedback(null, '', true)
    },
    async removeFeedback() {
      await this.clearFeedback()
      this.commentModalVisible = false
    },
    buildContext() {
      const item = this.item || {}
      const context = {
        alert_type: item.alert_type,
        booking_id: item.booking_id,
        booking_hash: item.booking_hash,
        vehicle_id: item.vehicle_id,
        start_base: item.start_base,
        alert_date: item.processed_time ? moment(item.processed_time).format('YYYY-MM-DD') : null,
      }
      const cleaned = {}
      Object.keys(context).forEach((key) => {
        if (context[key] != null && context[key] !== '')
          cleaned[key] = context[key]
      })
      return cleaned
    },
    async saveFeedback(rating, comment = '', clearing = false) {
      if (!this.item || !this.item.movement_vehicle_id)
        return
      this.submitting = true
      try {
        const payload = {
          movement_vehicle_id: this.item.movement_vehicle_id,
          rating: clearing ? null : rating,
          comment: clearing ? null : comment,
        }
        if (!clearing)
          payload.context = this.buildContext()

        const { value } = await save_alert_feedback(this.tenant, payload)
        if (!value || (value.alert && value.alert.variant === 'danger'))
          throw new Error('feedback save failed')
        if (clearing) {
          this.$delete(this.item, 'feedback')
          this.$delete(this.item, 'feedback_comment')
        } else {
          this.$set(this.item, 'feedback', rating)
          if (rating === 'concern' && comment)
            this.$set(this.item, 'feedback_comment', comment)
          else
            this.$delete(this.item, 'feedback_comment')
        }
        this.$emit('feedback-saved', { item: this.item, rating: clearing ? null : rating, comment: clearing ? null : comment })
      } catch (e) {
        console.warn('Failed to save alert feedback:', e)
        this.$emit('showAlert', 'error saving feedback, try again or contact support', 'danger', 5000)
      } finally {
        this.submitting = false
      }
    },
  },

  watch: {
    commentModalVisible(isOpen) {
      if (isOpen)
        document.addEventListener('mousedown', this.handleOutsideClick)
      else
        document.removeEventListener('mousedown', this.handleOutsideClick)
    },
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleOutsideClick)
  },
}
</script>

<style scoped>
.alert-feedback-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin-top: 2px;
}

.alert-feedback-btn {
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  font-size: 13px;
  padding: 1px 4px;
  line-height: 1;
  transition: color 0.15s;
}

.alert-feedback-btn:hover:not(:disabled) {
  color: #6c757d;
}

.alert-feedback-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* flag-only mode (audit mode off): the flag is the only control shown, so
   give its default (not-yet-flagged) state more presence than the muted
   gray used when it sits alongside thumbs up/down. */
.flag-only .alert-feedback-btn {
  color: #dc3545;
  font-size: 16px;
}

.alert-feedback-btn.positive.active {
  color: #198754;
}

.alert-feedback-btn.negative.active {
  color: #dc3545;
}

.alert-feedback-btn.concern.active {
  color: #dc3545;
}
</style>

<!-- popover content is teleported to <body> by BootstrapVue, so these rules
     must not be scoped or they'd never match the rendered markup. -->
<style>
.concern-popover {
  max-width: 440px;
  width: 440px;
  z-index: 1100;
}
.concern-popover .popover-body {
  padding: 10px 12px;
}
.concern-popover textarea {
  width: 100%;
  font-size: 14px;
}
.concern-popover-hint {
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
}
.concern-popover-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.concern-popover-remove-btn {
  margin-right: auto;
}
.concern-popover-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.concern-popover-close-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}
.concern-popover-close-btn:hover:not(:disabled) {
  color: #a71d2a;
}
.concern-popover-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
